import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ExternalLink, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, API } from "@/lib/api";

const inp = "w-full bg-slate-900 border border-border rounded-md px-2 py-1.5 text-sm outline-none focus:border-blue-500";

function RoleEditor({ r, onChange, onRemove }) {
  const set = (k, v) => onChange({ ...r, [k]: v });
  return (
    <div className="rounded-lg border border-border p-3 space-y-2" data-testid={`parsed-role-${r.id}`}>
      <div className="flex items-center gap-2">
        <span className="font-mono text-xs text-blue-300">{r.id}</span>
        <input className={inp} value={r.title} onChange={(e) => set("title", e.target.value)} placeholder="Title" data-testid={`parsed-role-title-${r.id}`} />
        <button onClick={onRemove} className="text-slate-500 hover:text-rose-400"><Trash2 size={14} /></button>
      </div>
      <div className="grid grid-cols-4 gap-2">
        <input className={`${inp} col-span-2`} value={r.company} onChange={(e) => set("company", e.target.value)} placeholder="Company" />
        <input className={inp} value={r.start} onChange={(e) => set("start", e.target.value)} placeholder="Start (Mon YYYY)" />
        <input className={inp} value={r.end} onChange={(e) => set("end", e.target.value)} placeholder="End / Present" />
      </div>
      <input className={inp} value={r.location} onChange={(e) => set("location", e.target.value)} placeholder="Location" />
      <textarea className={`${inp} min-h-[110px] font-mono text-xs`} value={(r.bullets || []).join("\n")}
        onChange={(e) => set("bullets", e.target.value.split("\n"))} placeholder="One bullet per line" />
    </div>
  );
}

export default function ParsedResumeEditor({ client, files, onSaved }) {
  const [p, setP] = useState(client.parsed_resume);
  useEffect(() => { setP(client.parsed_resume); }, [client.parsed_resume]);
  const resume = files.find((f) => f.kind === "resume");
  if (!p) return <div className="card-surface text-sm text-muted-foreground" data-testid="parsed-empty">Upload the old resume, then click "Parse resume & write call questions" above.</div>;

  const setRole = (i, r) => setP({ ...p, roles: p.roles.map((x, j) => (j === i ? r : x)) });
  const addRole = () => setP({ ...p, roles: [...p.roles, { id: `r${p.roles.length + 1}`, company: "", title: "", location: "", start: "", end: "", bullets: [] }] });
  const save = async () => {
    const clean = { ...p, roles: p.roles.map((r) => ({ ...r, bullets: r.bullets.filter((b) => b.trim()) })) };
    await api.put(`/clients/${client.id}/parsed-resume`, clean);
    toast.success("Parsed resume saved");
    onSaved();
  };

  return (
    <div className="grid grid-cols-2 gap-5" data-testid="intake-parsed-resume-editor">
      <div className="card-surface">
        <div className="flex items-center mb-3">
          <div className="label-xs">Original: {resume?.filename}</div>
          {resume && <a href={`${API}/files/${resume.id}`} target="_blank" rel="noreferrer" className="ml-auto text-xs text-blue-400 inline-flex items-center gap-1">Open file <ExternalLink size={11} /></a>}
        </div>
        <pre className="whitespace-pre-wrap text-xs font-mono text-slate-300 max-h-[900px] overflow-auto scroll-thin" data-testid="original-resume-text">{resume?.text}</pre>
      </div>
      <div className="card-surface space-y-3 max-h-[980px] overflow-auto scroll-thin">
        <div className="flex items-center">
          <div className="label-xs">Parsed structure (edit to correct)</div>
          <Button data-testid="parsed-save-button" size="sm" className="ml-auto bg-blue-600 hover:bg-blue-500 text-white" onClick={save}>Save corrections</Button>
        </div>
        {p.non_us_items_found?.length > 0 && (
          <div className="text-xs rounded-md border border-amber-500/30 bg-amber-500/5 p-2 text-amber-200 flex gap-2"><AlertTriangle size={13} /> Non-US items found (removed in draft): {p.non_us_items_found.join(", ")}</div>
        )}
        <div className="grid grid-cols-2 gap-2">
          {["name", "email", "phone", "location", "linkedin", "portfolio"].map((k) => (
            <input key={k} className={inp} value={p[k] || ""} placeholder={k} onChange={(e) => setP({ ...p, [k]: e.target.value })} />
          ))}
        </div>
        <textarea className={`${inp} min-h-[60px]`} value={p.summary} onChange={(e) => setP({ ...p, summary: e.target.value })} placeholder="Summary" />
        <div>
          <div className="label-xs mb-1">Skills (comma separated)</div>
          <textarea className={inp} value={p.skills.join(", ")} onChange={(e) => setP({ ...p, skills: e.target.value.split(",").map((s) => s.trim()) })} />
        </div>
        <div className="label-xs">Roles</div>
        {p.roles.map((r, i) => <RoleEditor key={i} r={r} onChange={(x) => setRole(i, x)} onRemove={() => setP({ ...p, roles: p.roles.filter((_, j) => j !== i) })} />)}
        <Button size="sm" variant="secondary" onClick={addRole} data-testid="parsed-add-role"><Plus size={12} className="mr-1" />Add role</Button>
        <div className="label-xs">Education</div>
        {p.education.map((e, i) => (
          <div key={i} className="grid grid-cols-4 gap-2">
            {["degree", "field", "school", "end"].map((k) => (
              <input key={k} className={inp} value={e[k] || ""} placeholder={k}
                onChange={(ev) => setP({ ...p, education: p.education.map((x, j) => (j === i ? { ...x, [k]: ev.target.value } : x)) })} />
            ))}
          </div>
        ))}
        <div className="label-xs">Certifications (one per line)</div>
        <textarea className={inp} value={p.certifications.join("\n")} onChange={(e) => setP({ ...p, certifications: e.target.value.split("\n") })} />
      </div>
    </div>
  );
}
